/**
 * Absence Service
 * 
 * API calls for absence domain
 */

import { apiClient } from '@/api/core/apiClient';
import { API_CONFIG } from '@/api/core/apiConfig';
import type { ApiResponse } from '@/api/shared/types';
import { buildRequiredQueryString, buildQueryString } from '@/api/shared/queryBuilder';
import type { HybridExportResult } from '@/types/fileExport.types';
import type {
  LC_ListAbsencesIn,
  LC_ListAbsencesOut,
  LC_ExportAbsencesIn,
  LC_ExportAbsencesManagementIn,
  LC_ListAbsencesByScopeIn,
  LC_ListAbsencesByScopeListOut,
  AbsenceListResult,
  AbsenceScopeListResult,
  LC_GetEmployeesForAbsenceIn,
  LC_GetEmployeesForAbsenceOut,
  EmployeesForAbsenceResult,
  LC_GetMedicalLeaveTypesOut,
  LC_CreateMedicalLeaveIn,
  LC_CreateMedicalLeaveOut,
  LC_GetMedicalLeaveDetailOut,
  LC_UpdateMedicalLeaveIn,
  LC_UpdateMedicalLeaveOut,
  MedicalLeaveDetail,
  LC_CalculateAbsenceDatesIn,
  LC_CalculateAbsenceDatesOut,
  LC_GetAbsenceWorkdayTypesOut,
  LC_GetSpecialLeaveTypesOut,
  LC_CreateSpecialLeaveIn,
  LC_CreateSpecialLeaveOut,
  LC_CreateVacationIn,
  LC_CreateVacationOut,
  LC_CalculateVacationDatesIn,
  LC_CalculateVacationDatesOut,
  LC_GetFractionalLeaveTypesOut,
  LC_GetCalculatedLateHoursOut,
  LC_CreateFractionalLeaveAssignmentIn,
  LC_CreateFractionalLeaveAssignmentOut,
} from './absence.types';
import {
  mapAbsenceListResponse,
  mapAbsenceScopeListResponse,
  mapEmployeesForAbsenceResponse,
  mapMedicalLeaveDetail,
} from './absence.mapper';


export class AbsenceService {
  /**
   * Get paginated list of absences with totals
   */
  async list(input: LC_ListAbsencesIn): Promise<ApiResponse<AbsenceListResult>> {
    const queryString = buildRequiredQueryString(
      { ...input },
      ['startDate', 'endDate', 'absenceType', 'status'],
    );
    const endpoint = `${API_CONFIG.ENDPOINTS.ABSENCE.LIST}${queryString}`;

    const response = await apiClient.get<LC_ListAbsencesOut>(endpoint);

    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }

    return {
      success: true,
      data: mapAbsenceListResponse(response.data),
      status: response.status,
    };
  }

  /**
   * Get paginated list of absences for management scope view (multi-employee)
   */
  async listByScope(input: LC_ListAbsencesByScopeIn): Promise<ApiResponse<AbsenceScopeListResult>> {
    const queryString = buildRequiredQueryString(
      { ...input },
      ['startDate', 'endDate', 'absenceType', 'status'],
    );
    const endpoint = `${API_CONFIG.ENDPOINTS.ABSENCE.SCOPE_LIST}${queryString}`;

    const response = await apiClient.get<LC_ListAbsencesByScopeListOut>(endpoint);

    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }

    return {
      success: true,
      data: mapAbsenceScopeListResponse(response.data),
      status: response.status,
    };
  }

  /**
   * Export absences to Excel file
   */
  async exportAbsences(input: LC_ExportAbsencesIn)
    : Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const searchParams = new URLSearchParams();
    searchParams.append('startDate', input.startDate);
    searchParams.append('endDate', input.endDate);
    searchParams.append('absenceType', input.absenceType);
    searchParams.append('status', input.status);
    searchParams.append('format', input.format);

    const endpoint = `${API_CONFIG.ENDPOINTS.ABSENCE.FILE}?${searchParams.toString()}`;
    return apiClient.downloadBlobWithFilename(endpoint, { method: 'GET' });
  }

  /**
   * Export the scope (management) absence listing to Excel, using the hybrid sync/async strategy:
   * the backend responds 200+blob under the sync threshold, or 202+jobId when it's exceeded.
   */
  async exportAbsencesManagement(
    input: LC_ExportAbsencesManagementIn,
  ): Promise<ApiResponse<HybridExportResult>> {
    const queryString = buildRequiredQueryString(
      { ...input },
      ['startDate', 'endDate', 'absenceType', 'status'],
    );
    const endpoint = `${API_CONFIG.ENDPOINTS.ABSENCE.MANAGEMENT_FILE}${queryString}`;

    return apiClient.downloadBlobOrAcceptJob(endpoint, { method: 'GET' });
  }

  /**
   * Get paginated list of employees available for absence creation
   */
  async getEmployeesForAbsence(
    input: LC_GetEmployeesForAbsenceIn,
  ): Promise<ApiResponse<EmployeesForAbsenceResult>> {
    const queryString = buildQueryString({ ...input });
    const endpoint = `${API_CONFIG.ENDPOINTS.ABSENCE.EMPLOYEES}${queryString}`;

    const response = await apiClient.get<LC_GetEmployeesForAbsenceOut>(endpoint);

    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }

    return {
      success: true,
      data: mapEmployeesForAbsenceResponse(response.data),
      status: response.status,
    };
  }

  /**
   * Get available medical leave types
   */
  async getMedicalLeaveTypes(): Promise<ApiResponse<LC_GetMedicalLeaveTypesOut>> {
    return apiClient.get<LC_GetMedicalLeaveTypesOut>(
      API_CONFIG.ENDPOINTS.MEDICAL_LEAVE.TYPES,
    );
  }

  /**
   * Create a new medical leave record
   */
  async createMedicalLeave(
    input: LC_CreateMedicalLeaveIn,
  ): Promise<ApiResponse<LC_CreateMedicalLeaveOut>> {
    return apiClient.post<LC_CreateMedicalLeaveOut>(
      API_CONFIG.ENDPOINTS.MEDICAL_LEAVE.CREATE,
      input,
    );
  }

  /**
   * Get the detail of an existing medical leave record for the ver/editar side panel (ASIS-5294).
   */
  async getMedicalLeaveById(
    id: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MedicalLeaveDetail>> {
    const response = await apiClient.get<LC_GetMedicalLeaveDetailOut>(
      API_CONFIG.ENDPOINTS.MEDICAL_LEAVE.BY_ID(id),
      { signal },
    );

    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }

    return {
      success: true,
      data: mapMedicalLeaveDetail(response.data),
      status: response.status,
    };
  }

  /**
   * Update an existing medical leave record's editable fields.
   */
  async updateMedicalLeave(
    id: number,
    input: LC_UpdateMedicalLeaveIn,
  ): Promise<ApiResponse<LC_UpdateMedicalLeaveOut>> {
    return apiClient.put<LC_UpdateMedicalLeaveOut>(
      API_CONFIG.ENDPOINTS.MEDICAL_LEAVE.BY_ID(id),
      input,
    );
  }

  /**
   * Calculate the end date (calendar days) and the return date (next business day) for an absence.
   * Shared across absence types whose end date counts calendar days (medical leave, special leave,
   * etc.) — vacation uses `calculateVacationDates` instead, since its end date counts business
   * days.
   */
  async calculateAbsenceDates(
    params: LC_CalculateAbsenceDatesIn,
  ): Promise<ApiResponse<LC_CalculateAbsenceDatesOut>> {
    const queryString = buildQueryString({ ...params });
    return apiClient.get<LC_CalculateAbsenceDatesOut>(
      `${API_CONFIG.ENDPOINTS.ABSENCE.CALCULATED_DATES}${queryString}`,
    );
  }

  /**
   * Get available workday types shared by absence creation flows
   */
  async getAbsenceWorkdayTypes(): Promise<ApiResponse<LC_GetAbsenceWorkdayTypesOut>> {
    return apiClient.get<LC_GetAbsenceWorkdayTypesOut>(
      API_CONFIG.ENDPOINTS.ABSENCE.WORKDAY_TYPES,
    );
  }

  /**
   * Get available special leave types (company-specific catalog)
   */
  async getSpecialLeaveTypes(): Promise<ApiResponse<LC_GetSpecialLeaveTypesOut>> {
    return apiClient.get<LC_GetSpecialLeaveTypesOut>(
      API_CONFIG.ENDPOINTS.SPECIAL_LEAVE.TYPES,
    );
  }

  /**
   * Create a new special leave record
   */
  async createSpecialLeave(
    input: LC_CreateSpecialLeaveIn,
  ): Promise<ApiResponse<LC_CreateSpecialLeaveOut>> {
    return apiClient.post<LC_CreateSpecialLeaveOut>(
      API_CONFIG.ENDPOINTS.SPECIAL_LEAVE.CREATE,
      input,
    );
  }

  /**
   * Calculate the end date (business days only) and the return date for a vacation period.
   * Vacation is the only absence whose end date skips holidays, so it has its own endpoint
   * instead of reusing ABSENCE.CALCULATED_DATES — and it returns both dates in a single round trip.
   */
  async calculateVacationDates(
    params: LC_CalculateVacationDatesIn,
  ): Promise<ApiResponse<LC_CalculateVacationDatesOut>> {
    const queryString = buildQueryString({ ...params });
    return apiClient.get<LC_CalculateVacationDatesOut>(
      `${API_CONFIG.ENDPOINTS.VACATION.CALCULATED_DATES}${queryString}`,
    );
  }

  /**
   * Create a new vacation period
   */
  async createVacation(
    input: LC_CreateVacationIn,
  ): Promise<ApiResponse<LC_CreateVacationOut>> {
    return apiClient.post<LC_CreateVacationOut>(
      API_CONFIG.ENDPOINTS.VACATION.CREATE,
      input,
    );
  }

  /**
   * Get available fractional leave types (company-specific catalog) for the assignment form.
   */
  async getFractionalLeaveTypesForAssignment():
    Promise<ApiResponse<LC_GetFractionalLeaveTypesOut>> {
    return apiClient.get<LC_GetFractionalLeaveTypesOut>(
      API_CONFIG.ENDPOINTS.FRACTIONAL_LEAVE_ASSIGNMENT.TYPES,
    );
  }

  /**
   * Calculate an employee's late hours (atrasos + ausencia entre jornada + salida anticipada)
   * for a specific day. Used as a read-only reference by the fractional leave form.
   */
  async getCalculatedLateHours(
    params: { employeeTargetId: number; date: string },
    signal?: AbortSignal,
  ): Promise<ApiResponse<LC_GetCalculatedLateHoursOut>> {
    const queryString = buildQueryString({ ...params });
    return apiClient.get<LC_GetCalculatedLateHoursOut>(
      `${API_CONFIG.ENDPOINTS.ABSENCE.CALCULATED_LATE_HOURS}${queryString}`,
      { signal },
    );
  }

  /**
   * Create a new fractional leave assignment record for an employee.
   */
  async createFractionalLeaveAssignment(
    input: LC_CreateFractionalLeaveAssignmentIn,
  ): Promise<ApiResponse<LC_CreateFractionalLeaveAssignmentOut>> {
    return apiClient.post<LC_CreateFractionalLeaveAssignmentOut>(
      API_CONFIG.ENDPOINTS.FRACTIONAL_LEAVE_ASSIGNMENT.CREATE,
      input,
    );
  }
}

export const absenceService = new AbsenceService();
